<template>
    <div class="text-sm break-words text-foreground">
        <!-- Enhanced simple error display -->
        <div class="space-y-3">
            <!-- Main error message -->
            <div v-if="mainErrorMessage" class="border-l-2 border-destructive pl-3">
                <h4 class="text-sm font-medium text-destructive mb-2">Message</h4>
                <p class="font-mono text-sm leading-relaxed whitespace-pre-wrap" v-html="highlightText(mainErrorMessage, message.searchTerm)"></p>
            </div>

            <!-- Additional details -->
            <div v-if="additionalDetails.length > 0" class="border-l-2 border-amber-500 pl-3">
                <h4 class="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">Additional Details</h4>
                <div class="space-y-1">
                    <div
                        v-for="(detail, index) in additionalDetails"
                        :key="index"
                        class="font-mono text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap"
                        v-html="highlightText(detail, message.searchTerm)"></div>
                </div>
            </div>

            <!-- Context information -->
            <div v-if="contextInfo.length > 0" class="border-l-2 border-blue-500 pl-3">
                <h4 class="text-sm font-medium text-blue-600 dark:text-blue-400 mb-2">Context</h4>
                <div class="space-y-1">
                    <div
                        v-for="(info, index) in contextInfo"
                        :key="index"
                        class="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap"
                        v-html="highlightText(info, message.searchTerm)"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    message: {
        type: Object,
        required: true
    },
    searchTerm: {
        type: String,
        default: ''
    }
});

// Extract main message (usually the first line or most prominent line)
const mainErrorMessage = computed(() => {
    const lines = props.message.lines.filter((line) => line.trim());
    if (lines.length === 0) return props.message.raw;

    // Look for the most descriptive line (usually contains keywords like 'error', 'failed', etc.)
    const errorKeywords = ['error', 'failed', 'exception', 'invalid', 'cannot', 'unable', 'denied'];
    const errorLine = lines.find((line) => errorKeywords.some((keyword) => line.toLowerCase().includes(keyword)));

    return errorLine || lines[0];
});

// Extract additional details (remaining lines)
const additionalDetails = computed(() => {
    const lines = props.message.lines.filter((line) => line.trim());
    if (lines.length <= 1) return [];

    return lines.slice(1).filter((line) => {
        // Filter out very generic lines
        const generic = ['error occurred', 'please try again', 'contact support'];
        return !generic.some((g) => line.toLowerCase().includes(g));
    });
});

// Extract context information (URLs, timestamps, request IDs, etc.)
const contextInfo = computed(() => {
    const context = [];
    const text = props.message.raw;

    // Extract URLs
    const urls = text.match(/https?:\/\/[^\s]+/g);
    if (urls) {
        urls.forEach((url) => context.push(`URL: ${url}`));
    }

    // Extract request IDs
    const requestIds = text.match(/(?:request|req|transaction|txn)[-_\s]*id:?\s*([a-f0-9-]{8,})/gi);
    if (requestIds) {
        requestIds.forEach((id) => context.push(`Request ID: ${id.split(/[:]\s*/)[1] || id}`));
    }

    // Extract timestamps
    const timestamps = text.match(/\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}/g);
    if (timestamps) {
        timestamps.forEach((ts) => context.push(`Timestamp: ${ts}`));
    }

    // Extract status codes
    const statusCodes = text.match(/(?:status|code|http)[-_\s]*:?\s*([45]\d{2})/gi);
    if (statusCodes) {
        statusCodes.forEach((code) => {
            const match = code.match(/([45]\d{2})/);
            if (match) context.push(`Status Code: ${match[1]}`);
        });
    }

    return [...new Set(context)]; // Remove duplicates
});

function highlightText(text, searchTerm) {
    // Check if text already contains HTML tags (from backend)
    const containsHtml = /<[^>]+>/g.test(text);
    
    if (containsHtml) {
        // If text already contains HTML, preserve it as-is and only apply search highlighting if needed
        let result = text;
        
        // Apply search highlighting if needed (but be careful not to break existing HTML)
        if (searchTerm && searchTerm.trim()) {
            // Only apply search highlighting to text content, not HTML tags
            const regex = new RegExp(`(${escapeRegex(searchTerm)})(?![^<]*>)`, 'gi');
            result = result.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 text-black dark:text-white">$1</mark>');
        }
        
        return result;
    }

    // Original highlighting logic for plain text only
    let highlighted = text;

    if (searchTerm && searchTerm.trim()) {
        const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
        highlighted = highlighted.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 text-black dark:text-white">$1</mark>');
    }

    // Basic text enhancements (only for plain text)
    highlighted = highlighted
        // Highlight important values in quotes FIRST (to avoid interfering with HTML attributes)
        .replace(/'([^'<>]+)'/g, '<span class="text-blue-600 dark:text-blue-400 font-medium">\'$1\'</span>')
        .replace(/"([^"<>]+)"/g, '<span class="text-blue-600 dark:text-blue-400 font-medium">"$1"</span>')
        // Then highlight error keywords with colon (more specific)
        .replace(/\berror:/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Error:</span>')
        .replace(/\bfailed:/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Failed:</span>')
        .replace(/\bexception:/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Exception:</span>')
        // Then highlight standalone keywords
        .replace(/\berror\b/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Error</span>')
        .replace(/\bfailed\b/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Failed</span>')
        .replace(/\bexception\b/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Exception</span>')
        .replace(/\binvalid\b/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Invalid</span>')
        .replace(/\bdenied\b/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Denied</span>')
        .replace(/\bunauthorized\b/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Unauthorized</span>')
        .replace(/\bforbidden\b/gi, '<span class="text-red-600 dark:text-red-400 font-medium">Forbidden</span>')
        // Highlight success keywords
        .replace(/\bsuccess\b/gi, '<span class="text-green-600 dark:text-green-400 font-medium">Success</span>')
        .replace(/\bcompleted\b/gi, '<span class="text-green-600 dark:text-green-400 font-medium">Completed</span>')
        .replace(/\bvalid\b/gi, '<span class="text-green-600 dark:text-green-400 font-medium">Valid</span>')
        .replace(/\bauthorized\b/gi, '<span class="text-green-600 dark:text-green-400 font-medium">Authorized</span>')
        .replace(/\ballowed\b/gi, '<span class="text-green-600 dark:text-green-400 font-medium">Allowed</span>');

    return highlighted;
}

function escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
</script>
